import { useEffect, useState } from 'react';
import { detectPhone } from '../components/videoEditor/phoneLayout';

/**
 * Safari's bars on a phone, done as the editor does them (LessonVideoEditor,
 * spec 14.9 and 14.10), on every phone, as the editor does:
 *
 * - The room is sized to the visible viewport (.room-fill in index.css:
 *   --vv-height, 100dvh as the fallback), so the stage and the controls stay
 *   above the address bar and tab strip whether they show or not.
 * - The document is made several screens taller than the room
 *   (html[data-room-covering]), so a swipe up scrolls it and Safari collapses
 *   its bars. The room is fixed, so nothing behind it shows. The page's
 *   scroll is put back when the room goes.
 * - In landscape with the bars showing, a one-time "Swipe up for full screen"
 *   hint, never in a home screen app, remembered on the device. It fades
 *   after 4 seconds or on the first swipe.
 *
 * Element full screen, where the browser has it (iOS 26 Safari on iPhone
 * too, an iPad, Android), is asked for on top of this by the room as the
 * phone turns sideways and again on the first touch: a turn is not the
 * gesture the API needs, so until that touch Safari's bars show, and this
 * keeps the room above them. Walked 27 October (docs/walks/2026-10-27-room).
 */
export const ROOM_BARS_HINT = 'Swipe up for full screen';
const SEEN_KEY = 'room.barsHintSeen';
let shownThisPage = false;

/** A phone: the editor's way with Safari's bars applies, with or without element full screen. */
export function needsChromeFallback(): boolean {
  if (typeof document === 'undefined') return false;
  return detectPhone().phone;
}

export function useRoomChrome(landscape: boolean): { fill: boolean; hint: 'off' | 'on' | 'fading' } {
  const [fill] = useState(needsChromeFallback);

  useEffect(() => {
    if (!fill) return;
    const html = document.documentElement;
    const y = window.scrollY;
    html.setAttribute('data-room-covering', '');
    window.scrollTo(0, 0);
    const reread = () => requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    reread();
    return () => {
      html.removeAttribute('data-room-covering');
      window.scrollTo(0, y);
      reread();
    };
  }, [fill]);

  const [hint, setHint] = useState<'off' | 'on' | 'fading'>('off');
  useEffect(() => {
    if (!fill || !landscape || shownThisPage || document.fullscreenElement) return;
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    if (standalone) return;
    try { if (localStorage.getItem(SEEN_KEY) === '1') return; } catch { /* treat as not seen */ }
    const vv = window.visualViewport;
    if (!vv) return;
    let fadeTimer: ReturnType<typeof setTimeout> | null = null;
    let offTimer: ReturnType<typeof setTimeout> | null = null;
    const dismiss = () => {
      if (offTimer) return;
      setHint('fading');
      offTimer = setTimeout(() => setHint('off'), 300);
      window.removeEventListener('scroll', dismiss);
      window.removeEventListener('touchmove', dismiss);
    };
    const check = () => {
      if (shownThisPage) return;
      // The bars are showing when the visible height is clearly short of the screen's.
      const screenH = Math.min(window.screen.width, window.screen.height);
      if (document.fullscreenElement || vv.height >= screenH - 40) return;
      shownThisPage = true;
      try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* remembered for this page only */ }
      setHint('on');
      fadeTimer = setTimeout(dismiss, 4000);
      window.addEventListener('scroll', dismiss, { passive: true });
      window.addEventListener('touchmove', dismiss, { passive: true });
    };
    const raf = requestAnimationFrame(check);
    vv.addEventListener('resize', check);
    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener('resize', check);
      window.removeEventListener('scroll', dismiss);
      window.removeEventListener('touchmove', dismiss);
      if (fadeTimer) clearTimeout(fadeTimer);
      if (offTimer) clearTimeout(offTimer);
    };
  }, [fill, landscape]);

  return { fill, hint };
}
